import api from "@/api/api";
import { toast } from "@/components/utils/ui_v4/alerts/toast/toast";
import alturaDaJanela from "@/helpers/ResponsiveHeightReativo";
import { computed, onMounted, ref, watch } from "vue";
import { formatCNPJ } from "../utils/stringUtils";
import useTableScroll from "./useTableScroll";

export default function useVisualizarFornecedoresDialog(props, emit) {
  const isLoading = ref(false);
  const isLoadingMore = ref(false);
  const fornecedores = ref([]);
  const selected = ref([]);
  const offset = ref(0);
  const limitInicial = 40;
  const limit = ref(limitInicial);
  const hasMore = ref(true);
  const filtros = ref({});
  const cotacaoTitulo = ref('');

  const alturaTabela = computed(() => alturaDaJanela.value - 350);

  const headers = [
    { text: "Fornecedor", value: "fornecedor", sortable: true },
    { text: "CNPJ", value: "cnpj", sortable: true },
    { text: "Cód. Fornecedor", value: "cod_fornecedor", sortable: true },
    { text: "Razão social", value: "razao_social", sortable: true },
    { text: "Nome fantasia", value: "nome_fantasia", sortable: true },
  ];

  const getFornecedores = async (reset = false) => {
    if (reset) {
      offset.value = 0;
      limit.value = limitInicial;
      fornecedores.value = [];
      hasMore.value = true;
    }

    if (!hasMore.value && !reset) {
      return;
    }

    if (!props.idCotacao) {
      toast.error("ID da cotação não informado.");
      return;
    }

    if (reset) {
      isLoading.value = true;
    } else {
      isLoadingMore.value = true;
    }

    try {
      const currentLimit = offset.value === 0 ? limitInicial : offset.value + limitInicial;

      const params = new URLSearchParams();
      params.append('offset', offset.value.toString());
      params.append('limit', currentLimit.toString());

      Object.keys(filtros.value).forEach(key => {
        const value = filtros.value[key];
        if (value && value.trim() !== '') {
          params.append(key, value.trim());
        }
      });

      const queryString = params.toString();
      const endpointBase = `/cotacao/listar_todos_fornecedores/${props.idCotacao}`;
      const endpoint = queryString ? `${endpointBase}?${queryString}` : endpointBase;
      const { data: response } = await api.get(endpoint);

      const fornecedoresData = response.fornecedores || response || [];

      const scrollInfo = response.scroll || {};
      const nextOffset = scrollInfo.next_offset;
      const backendHasMore = scrollInfo.has_more !== undefined ? scrollInfo.has_more : true;

      if (fornecedoresData.length === 0) {
        hasMore.value = false;
      } else {
        const novosFornecedores = fornecedoresData.map((fornecedor, index) => {
          const itemId = fornecedor.id || fornecedor.idfornecedor || (offset.value + index);

          return {
            ...fornecedor,
            _itemId: itemId,
            fornecedor: fornecedores.value.length + index + 1,
            cod_fornecedor: fornecedor.cod_fornecedor || fornecedor.id || '',
            contatos: fornecedor.contatos || fornecedor.contatos_count || fornecedor.qtd_contatos || (fornecedor.representantes && fornecedor.representantes.length) || 0,
          };
        });

        if (reset) {
          fornecedores.value = novosFornecedores;
          offset.value = nextOffset !== undefined ? nextOffset : novosFornecedores.length;
          limit.value = offset.value + limitInicial;
          hasMore.value = backendHasMore !== undefined ? backendHasMore : (novosFornecedores.length > 0);
        } else {
          fornecedores.value = [...fornecedores.value, ...novosFornecedores];
          offset.value = nextOffset !== undefined ? nextOffset : fornecedores.value.length;
          limit.value = offset.value + limitInicial;
          hasMore.value = backendHasMore !== undefined ? backendHasMore : (novosFornecedores.length > 0);
        }
      }
    } catch (error) {
      toast.error("Não foi possível carregar os fornecedores!");
    } finally {
      isLoading.value = false;
      isLoadingMore.value = false;
    }
  };

  const loadMore = async () => {
    if (!isLoadingMore.value && hasMore.value && !isLoading.value) {
      await getFornecedores(false);
    }
  };

  const aplicarFiltros = async (novosFiltros) => {
    filtros.value = { ...novosFiltros };
    await getFornecedores(true);
  };

  const buscarTituloCotacao = async () => {
    if (props.idCotacao) {
      try {
        const { data: cotacaoResponse } = await api.get(`/cotacao/criar_cotacao_buscar_detalhes_cotacao?cotacao_transacao_id=${props.idCotacao}`);
        cotacaoTitulo.value = (cotacaoResponse && cotacaoResponse.cotacao && cotacaoResponse.cotacao.cotacao_titulo) || '';
      } catch (error) {
        toast.error("Erro ao buscar título da cotação.");
      }
    }
  };

  watch(() => props.visible,
    async (newVisible) => {
      if (newVisible) {
        await buscarTituloCotacao();
        await getFornecedores(true);
      }
    },
    { immediate: true }
  );

  // ============ SEARCH LOGIC ============
  const searchQuery = ref('');
  let searchTimeout = null;

  // Debounce para busca - aplicar filtros após parar de digitar
  watch(searchQuery, (newValue) => {
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }

    searchTimeout = setTimeout(() => {
      const filtros = {};

      if (newValue && newValue.trim() !== '') {
        const query = newValue.trim();

        // Verificar se é CNPJ (apenas números)
        if (/^\d+$/.test(query.replace(/\D/g, ''))) {
          filtros.cnpj = query.replace(/\D/g, '');
        } else {
          // Caso contrário, buscar por razão social
          filtros.razao_social = query;
        }
      }

      aplicarFiltros(filtros);
    }, 500); // Aguardar 500ms após parar de digitar
  });

  const {
    dataTable,
    scrollSentinel,
    scrollContainer,
    onItemsChange,
    onVisibleChange
  } = useTableScroll({
    loadMore,
    hasMore,
    isLoadingMore,
    isLoading,
    items: fornecedores
  });

  watch(() => fornecedores.value.length, onItemsChange);
  watch(() => props.visible, onVisibleChange);

  onMounted(() => {
    if (props.visible) {
      setTimeout(() => {
        onVisibleChange(true);
      }, 800);
    }
  });

  const isAdicionando = ref(false);

  const nomeFornecedorExibicao = (f) =>
    (f && (f.razao_social || f.nome_fantasia)) ||
    (f && (f.id || f.idfornecedor || f._itemId) != null
      ? `Fornecedor ID ${f.id || f.idfornecedor || f._itemId}`
      : "Fornecedor");

  const mensagemErroApi = (error) => {
    const data = error && error.response && error.response.data;
    if (!data) return null;
    if (typeof data.message === "string" && data.message.trim()) return data.message.trim();
    if (typeof data.error === "string" && data.error.trim()) return data.error.trim();
    return null;
  };

  const handleAdicionarFornecedores = async () => {
    if (!props.idCotacao) {
      toast.error("ID da cotação não fornecido!");
      return;
    }

    if (selected.value.length === 0) {
      toast.warning("Selecione pelo menos um fornecedor!");
      return;
    }

    isAdicionando.value = true;
    const fornecedoresAdicionados = [];
    const fornecedoresComErro = [];

    try {
      for (const fornecedorSelecionado of selected.value) {
        try {
          const idFornecedor = fornecedorSelecionado.id || fornecedorSelecionado.idfornecedor || fornecedorSelecionado._itemId;

          if (!idFornecedor) {
            fornecedoresComErro.push({
              fornecedor: fornecedorSelecionado,
              message: "ID do fornecedor não encontrado.",
              errorCode: "",
            });
            continue;
          }

          await api.post(`/cotacao/adicionar_fornecedor_cotacao/${props.idCotacao}/${idFornecedor}`);
          fornecedoresAdicionados.push(fornecedorSelecionado);
        } catch (error) {
          const data = error && error.response && error.response.data;
          fornecedoresComErro.push({
            fornecedor: fornecedorSelecionado,
            message: mensagemErroApi(error) || "Não foi possível adicionar este fornecedor à cotação.",
            errorCode: data && typeof data.error === "string" ? data.error : "",
          });
        }
      }

      // Processar resultados
      if (fornecedoresAdicionados.length > 0) {
        toast.success(`${fornecedoresAdicionados.length} fornecedor(es) adicionado(s) com sucesso!`);
        selected.value = [];

        if (emit) {
          emit('fornecedores-adicionados', fornecedoresAdicionados.length);
        }

        if (props.handleClose) {
          props.handleClose();
        }
      }

      const erroEhCnpjObrigatorio = (e) =>
        e.errorCode === "CNPJ obrigatório" ||
        (e.message && /CNPJ/i.test(e.message));

      if (fornecedoresComErro.length > 0 && fornecedoresAdicionados.length === 0) {
        const todosCnpj = fornecedoresComErro.every(erroEhCnpjObrigatorio);
        if (todosCnpj) {
          const nomes = fornecedoresComErro
            .map((e) => nomeFornecedorExibicao(e.fornecedor))
            .join(", ");
          const texto = fornecedoresComErro[0].message;
          toast.error(`${texto} Não é possível adicionar: ${nomes}.`);
        } else if (fornecedoresComErro.length === 1) {
          const e = fornecedoresComErro[0];
          toast.error(
            `${e.message} (${nomeFornecedorExibicao(e.fornecedor)})`
          );
        } else {
          const primeiro = fornecedoresComErro[0];
          toast.error(
            `${primeiro.message} — ${nomeFornecedorExibicao(primeiro.fornecedor)} e mais ${fornecedoresComErro.length - 1} fornecedor(es).`
          );
        }
      } else if (fornecedoresComErro.length > 0) {
        const todosCnpj = fornecedoresComErro.every(erroEhCnpjObrigatorio);
        if (todosCnpj) {
          const nomes = fornecedoresComErro
            .map((e) => nomeFornecedorExibicao(e.fornecedor))
            .join(", ");
          toast.warning(
            `${fornecedoresComErro[0].message} Não adicionados: ${nomes}. Os demais foram vinculados à cotação.`
          );
        } else {
          toast.warning(
            `${fornecedoresComErro.length} fornecedor(es) não puderam ser adicionado(s), mas ${fornecedoresAdicionados.length} foram adicionados com sucesso.`
          );
        }
      }
    } catch (error) {

      if (fornecedoresAdicionados.length === 0) {
        toast.error("Erro ao adicionar fornecedores à cotação!");
      }
    } finally {
      isAdicionando.value = false;
    }
  };

  return {
    isLoading,
    isLoadingMore,
    fornecedores,
    selected,
    headers,
    alturaTabela,
    hasMore,
    cotacaoTitulo,
    getFornecedores,
    loadMore,
    aplicarFiltros,
    // Search
    searchQuery,
    formatCNPJ,
    // Scroll handling
    scrollContainer,
    dataTable,
    scrollSentinel,
    // Add suppliers
    isAdicionando,
    handleAdicionarFornecedores,
  };
}
